import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useCategories } from "@/context/CategoriesContext";
import { Users, Timer, AlertTriangle, ClipboardList, Trophy, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";

export default function Dashboard() {
  const { ALL_CATEGORIES, CATEGORY_LABEL } = useCategories();
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setSummary(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
        <div>
          <h1 className="font-display font-black text-3xl tracking-tight" data-testid="dashboard-title">Dashboard</h1>
          <p className="text-[#7A7D8C] mt-1 text-sm">Live overview of competitor status.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/admin/events" data-testid="quick-events" className="px-4 py-2 rounded-full bg-[#FF9F1C] text-white text-sm font-semibold shadow-[0_8px_20px_rgb(255,159,28,0.3)]">Open events</Link>
          <Link to="/admin/competitors" data-testid="quick-roster" className="px-4 py-2 rounded-full border border-slate-200 text-sm font-semibold hover:border-[#FF9F1C] hover:text-[#FF9F1C]">Global roster</Link>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={<Users className="w-4 h-4" />} label="Total competitors" value={summary?.total ?? "—"} tid="kpi-total" />
        <Kpi icon={<CheckCircle2 className="w-4 h-4" />} label="Arrived today" value={summary?.by_status?.arrived ?? "—"} tid="kpi-arrived" />
        <Kpi icon={<Timer className="w-4 h-4" />} label="Waiting for prelims" value={summary?.waiting_prelim ?? "—"} tid="kpi-waiting" />
        <Kpi icon={<AlertTriangle className="w-4 h-4" />} label="Category mismatches" value={summary?.mismatched ?? "—"} tid="kpi-mismatch" />
      </div>

      <div className="mt-8 grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-slate-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-bold text-lg">Competitors by category</h2>
            <Link to="/admin/competitors" className="text-sm text-[#FF9F1C] font-semibold">Manage →</Link>
          </div>
          <ul className="space-y-3">
            {[...ALL_CATEGORIES, "unassigned"].map((k) => (
              <li key={k} className="flex items-center justify-between bg-[#FDFFFC] rounded-xl px-4 py-3 border border-slate-100">
                <span className="text-sm font-semibold">{k === "unassigned" ? "Not yet assigned" : CATEGORY_LABEL[k]}</span>
                <span className="font-display font-black text-xl" data-testid={`bycat-${k}`}>{summary?.by_category?.[k] ?? 0}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 p-6">
          <h2 className="font-display font-bold text-lg mb-4">Quick actions</h2>
          <div className="grid gap-3">
            <QuickLink to="/admin/events" icon={<Trophy className="w-4 h-4" />} title="Events" desc="Open a competition to manage rounds, roster, and check-in QR." />
            <QuickLink to="/admin/competitors" icon={<ClipboardList className="w-4 h-4" />} title="Global roster" desc="Search across every event, import CSV, export data." />
            <QuickLink to="/admin/archive" icon={<Timer className="w-4 h-4" />} title="Past events archive" desc="Look back at previous winners and full standings." />
          </div>
        </div>
      </div>
    </div>
  );
}

function Kpi({ icon, label, value, tid }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-5" data-testid={tid}>
      <div className="flex items-center gap-2 text-[#7A7D8C] text-xs uppercase tracking-wider font-semibold">{icon} {label}</div>
      <div className="mt-2 font-display font-black text-3xl">{value}</div>
    </div>
  );
}

function QuickLink({ to, icon, title, desc }) {
  return (
    <Link to={to} className="flex items-center gap-4 p-4 rounded-xl border border-slate-100 hover:border-[#FF9F1C] hover:bg-[#FFBF69]/10 transition-colors">
      <div className="w-10 h-10 rounded-xl bg-[#FF9F1C] text-white grid place-items-center">{icon}</div>
      <div className="flex-1">
        <div className="font-semibold">{title}</div>
        <div className="text-xs text-[#7A7D8C]">{desc}</div>
      </div>
    </Link>
  );
}
